import { loadLastAppearance } from '../character/appearance.js';
import {
  repairWorldName,
  repairWorldSave,
  WORLD_NAME_MAX_LENGTH,
  worldNameTaken,
} from '../world-storage.js';

export function formatSaveTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function formatRepairMessage(name, repaired) {
  const parts = [];
  if (repaired.removedTiles) {
    parts.push(`${repaired.removedTiles} incompatible ${repaired.removedTiles === 1 ? 'tile' : 'tiles'}`);
  }
  if (repaired.removedCells) {
    parts.push(`${repaired.removedCells} orphaned terrain ${repaired.removedCells === 1 ? 'cell' : 'cells'}`);
  }
  if (repaired.removedCreatures) {
    parts.push(`${repaired.removedCreatures} incompatible ${repaired.removedCreatures === 1 ? 'creature' : 'creatures'}`);
  }
  if (repaired.removedNpcs) {
    parts.push(`${repaired.removedNpcs} deleted ${repaired.removedNpcs === 1 ? 'NPC' : 'NPCs'}`);
  }
  return parts.length ? `Repaired ${name} by removing ${parts.join(' and ')}.` : '';
}

export class GameMenu {
  constructor(game) {
    this.game = game;
    this.root = null;
    this.openButton = null;
    this.closeButton = null;
    this.form = null;
    this.nameInput = null;
    this.createButton = null;
    this.error = null;
    this.characterButton = null;
    this.loadList = null;
    this.hudStatus = null;
    this.menuStatus = null;
    this.actions = null;
    this.hudPlayer = null;
    this.hudWorld = null;
    this.menuWorld = null;
    this.menuPlayer = null;
  }

  init() {
    this.root = document.getElementById('game-menu');
    this.openButton = document.getElementById('game-menu-open');
    this.closeButton = document.getElementById('game-menu-close');
    this.form = document.getElementById('new-game-form');
    this.nameInput = document.getElementById('new-world-name');
    this.createButton = document.getElementById('new-game-create');
    this.error = document.getElementById('new-game-error');
    this.characterButton = document.getElementById('game-menu-character');
    this.loadList = document.getElementById('load-game-list');
    this.hudStatus = document.getElementById('world-status');
    this.menuStatus = document.getElementById('game-menu-status');
    this.actions = document.querySelector('.game-menu__actions');
    this.hudPlayer = document.getElementById('player-name');
    this.hudWorld = document.getElementById('world-name');
    this.menuWorld = document.getElementById('game-menu-world');
    this.menuPlayer = document.getElementById('game-menu-player');

    this.openButton.addEventListener('click', () => this.open());
    this.closeButton.addEventListener('click', () => this.close());
    this.root.addEventListener('click', (event) => {
      if (event.target === this.root) this.close();
    });
    this.form.addEventListener('submit', (event) => this._onNewGameSubmit(event));
    this.nameInput.maxLength = WORLD_NAME_MAX_LENGTH;
    this.nameInput.addEventListener('input', () => this.syncNewGameForm());
    this.characterButton.addEventListener('click', () => {
      if (!this.game.currentSave) return;
      this.close();
      this.game.player.keys.clear();
      this.game.customizer.show(this.game.player.appearance, 'edit');
    });
    this.loadList.addEventListener('click', (event) => {
      const button = event.target.closest('[data-save-id]');
      if (!button || button.disabled) return;
      this.game._loadWorld(button.dataset.saveId);
    });
    window.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || this.game.customizer?.open || !this.isOpen()) return;
      event.preventDefault();
      this.close();
    });
    this.refresh();
  }

  isOpen() {
    return Boolean(this.root && !this.root.hidden);
  }

  open() {
    this.game.player.keys.clear();
    this.refresh(this.game.gameMenuStatus);
    this.game.gameMenuStatus = '';
    this.root.hidden = false;
    this.openButton.setAttribute('aria-expanded', 'true');
    this.nameInput.focus();
    this.nameInput.select();
  }

  close(force = false) {
    if (!force && !this.game.currentSave) return;
    this.root.hidden = true;
    this.openButton.setAttribute('aria-expanded', 'false');
    this.game.player.keys.clear();
  }

  clearNewWorldName() {
    if (this.nameInput) this.nameInput.value = '';
  }

  updateWorldName() {
    const worldName = this.game.currentSave?.name ?? '';
    const playerName = this.game.player.appearance.name;
    if (this.hudPlayer) this.hudPlayer.textContent = playerName;
    if (this.hudWorld) this.hudWorld.textContent = worldName;
    if (this.menuWorld) this.menuWorld.textContent = worldName || 'No world yet';
    if (this.menuPlayer) {
      this.menuPlayer.textContent = playerName ? `Playing as ${playerName}` : '';
    }
  }

  syncNewGameForm() {
    const name = repairWorldName(this.nameInput.value);
    const taken = worldNameTaken(this.game._menuSaves ?? this.game.storage.list(), name);
    this.createButton.disabled = !name || taken;
    this.error.textContent = name && taken
      ? 'That world name is already in use.'
      : '';
  }

  refresh(message = '') {
    const { game } = this;
    this.loadList.replaceChildren();
    let invalidCount = 0;
    let repairableCount = 0;
    const saves = game.storage.list()
      .sort((a, b) => String(b?.updatedAt).localeCompare(String(a?.updatedAt)));
    game._menuSaves = saves;
    if (!saves.length) {
      const empty = document.createElement('p');
      empty.className = 'load-game-empty';
      empty.textContent = 'No saved worlds yet.';
      this.loadList.append(empty);
    }
    for (const save of saves) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'load-game-item';
      button.dataset.saveId = typeof save?.id === 'string' ? save.id : '';
      button.setAttribute('role', 'listitem');
      const title = document.createElement('span');
      title.className = 'load-game-item__name';
      const meta = document.createElement('span');
      meta.className = 'load-game-item__meta';
      const isCurrent = save?.id === game.currentSave?.id;
      let notes = '';
      let entry = game._repairNotesCache.get(save?.id);
      if (!entry || entry.updatedAt !== save?.updatedAt) {
        try {
          const repair = repairWorldSave(game.tileCatalog, save);
          const repairNotes = [
            repair.removedTiles ? `${repair.removedTiles} tiles` : '',
            repair.removedCells ? `${repair.removedCells} cells` : '',
            repair.removedCreatures ? `${repair.removedCreatures} creatures` : '',
          ].filter(Boolean);
          entry = { updatedAt: save.updatedAt, notes: repairNotes.join(', ') };
        } catch {
          entry = { updatedAt: save?.updatedAt, notes: null };
        }
        if (typeof save?.id === 'string') game._repairNotesCache.set(save.id, entry);
      }
      if (entry.notes !== null) {
        title.textContent = save.name;
        if (entry.notes) {
          notes = `Repairable: ${entry.notes}`;
          repairableCount++;
        }
        if (isCurrent) {
          button.disabled = true;
          button.setAttribute('aria-current', 'true');
          notes = notes ? `Current · ${notes}` : 'Current world';
        }
      } else {
        title.textContent = save?.name ?? 'Unknown world';
        notes = 'Unavailable';
        button.disabled = true;
        invalidCount++;
      }
      meta.textContent = [formatSaveTime(save?.updatedAt), notes].filter(Boolean).join(' · ');
      button.append(title, meta);
      this.loadList.append(button);
    }
    const notices = [];
    if (message) notices.push(message);
    if (repairableCount) notices.push(`${repairableCount} saved ${repairableCount === 1 ? 'world has' : 'worlds have'} tiles that will be removed when loaded.`);
    if (invalidCount) notices.push(`${invalidCount} saved ${invalidCount === 1 ? 'world is' : 'worlds are'} malformed and cannot be loaded.`);
    const noticeText = notices.join(' ');
    this.hudStatus.textContent = noticeText;
    this.menuStatus.textContent = noticeText;
    this.characterButton.disabled = !game.currentSave;
    this.actions.hidden = !game.currentSave;
    this.syncNewGameForm();
    this.updateWorldName();
  }

  _onNewGameSubmit(event) {
    event.preventDefault();
    this.syncNewGameForm();
    const name = repairWorldName(this.nameInput.value);
    if (!name || worldNameTaken(this.game.storage.list(), name)) return;
    this.game.pendingWorldName = name;
    this.game._saveNow();
    this.close(true);
    this.game.player.keys.clear();
    this.game.customizer.show(loadLastAppearance(), 'new-world');
  }
}
